<template>
  <onboarding-container
    :include-back-button="!hasWhiteGloveService"
    @goBack="goBack"
    :width="600"
  >
    <template v-if="hasWhiteGloveService">
      <h2 class="onboarding--heading">Welcome To Forefront</h2>

      <div class="video-container mb-100">
        <div class="video">
          <iframe
            class="iframe"
            src="https://www.youtube.com/embed/KGz4sevq0Rg"
            frameborder="0"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      </div>
    </template>

    <h2 v-else class="onboarding--heading">White Glove Service</h2>

    <ol class="onboarding--content">
      <li
        v-for="(question, index) in questions"
        :key="index"
        class="mb-150"
      >
        <p class="onboarding--question">{{ question.text }}</p>

        <questionnaire-options
          v-model="question.value"
          :answers="question.answers"
        />
      </li>
    </ol>

    <div>
      <p class="onboarding--text">Thanks for answering these questions. We look forward to helping get your account fully set up for you.</p>
      <p v-if="hasWhiteGloveService" class="onboarding--text">Please click the button below to schedule your appointment in Calendly in the new tab that opens.</p>
      <p v-else class="onboarding--text">Please click the button below to process your $997 payment. Then schedule your appointment in Calendly in the new tab that opens.</p>
      <p class="onboarding--text">Finally come back to this tab to continue into Forefront.</p>
    </div>

    <div class="flex flex-centered my-200">
      <button-default
        v-if="hasWhiteGloveService"
        @click="scheduleMeeting"
        size=""
      >Schedule a Meeting
      </button-default>

      <button-default
        v-else
        @click="makePayment"
        size=""
        :loading="paymentProcessing"
      >Process $997 Payment and Schedule a Meeting
      </button-default>
    </div>
  </onboarding-container>
</template>

<script>
import {defineComponent} from 'vue'
import OnboardingContainer from "./component/OnboardingContainer.vue";
import QuestionnaireOptions from "./component/QuestionnaireOptions.vue";
import api from "../../api";

export default defineComponent({
  name: "OnboardingWhiteGlovePage",
  metaInfo: {
    title: "White Glove Questionnaire"
  },
  components: {QuestionnaireOptions, OnboardingContainer},
  data() {
    return {
      paymentProcessing: false,
      questions: {
        phone_system: {
          value: null,
          text:
            'We integrate with smrtPhone and CallRail to make and receive calls in Forefront. Which phone system would you like to use?',
          answers: {
            "smrtPhone": {
              "label": "smrtPhone",
              "value": 1
            },
            "CallRail": {
              "label": "CallRail",
              "value": 2
            },
            "SomethingElse": {
              "label": "Something Else",
              "value": 3
            },
          },
        },
        email_system: {
          value: null,
          text:
            'We integrate with Gmail and allow custom email domains so you can send emails to contacts. Which email system would you like to use?',
          answers: {
            "Gmail": {
              "label": "Gmail (or Google Workspaces)",
              "value": 1
            },
            "CustomDomain": {
              "label": "Use Custom Domain",
              "value": 2
            },
            "SomethingElse": {
              "label": "Something Else",
              "value": 3
            },
          },
        },
        calendar: {
          value: null,
          text:
            'Would you like to integrate with a company Google Calendar?',
          answers: {
            "yes": {
              "label": "Yes",
              "value": 1
            },
            "no": {
              "label": "No",
              "value": 2
            },
            "different": {
              "label": "I use a different calendar system",
              "value": 3
            },
          },
        },
        launch_control: {
          value: null,
          text:
            'Do you have a Launch Control subscription?',
          answers: {
            "yes": {
              "label": "Yes",
              "value": 1
            },
            "noInterested": {
              "label": "No, but I'm interested",
              "value": 2
            },
            "noNotInterested": {
              "label": "No, not interested",
              "value": 3
            },
          },
        },
        webhook: {
          value: null,
          text:
            'Do have any a website form you\'d like to connect to Forefront?',
          answers: {
            "yes": {
              "label": "Yes",
              "value": 1
            },
            "no": {
              "label": "No",
              "value": 2
            },
          },
        },
        import: {
          value: null,
          text:
            'Do you have leads from another system you\'d like to import?',
          answers: {
            "yes": {
              "label": "Yes",
              "value": 1
            },
            "no": {
              "label": "No",
              "value": 2
            },
          },
        },
        sequences: {
          value: null,
          text:
            'Would you like to use some of our Follow Up Sequences?',
          answers: {
            "yes": {
              "label": "Yes",
              "value": 1
            },
            "no": {
              "label": "No",
              "value": 2
            },
          },
        },
        invite_users: {
          value: null,
          text:
            'Do you have any team members you\'d like to invite?',
          answers: {
            "yes": {
              "label": "Yes",
              "value": 1
            },
            "no": {
              "label": "No",
              "value": 2
            },
          },
        },
        campaigns: {
          value: null,
          text:
            'Do you have any existing marketing campaigns?',
          answers: {
            "yes": {
              "label": "Yes",
              "value": 1
            },
            "no": {
              "label": "No",
              "value": 2
            },
          },
        },
      },
    }
  },
  computed: {
    hasWhiteGloveService() {
      return this.$store.state.onboarding.hasWhiteGloveService;
    },
    questionnaire() {
      let data = {};

      const keys = Object.keys(this.questions);
      keys.forEach((key) => {
        const question = this.questions[key];
        let answer = null;

        if(question.value) {
          const answerKeys = Object.keys(question.answers);
          answerKeys.forEach((answerKey) => {
            if(question.answers[answerKey].value === question.value) {
              answer = question.answers[answerKey].label
            }
          })
        }

        data[question.text] = answer;
      });

      return data;
    }
  },
  methods: {
    goBack() {
      this.$router.push({name: 'onboarding-welcome'})
    },
    makePayment() {
      this.paymentProcessing = true;

      api.supportServices.processWhiteGlovePayment(this.$store.getters.account.id, {'questionnaire': this.questionnaire}).then(() => {
        this.scheduleMeeting();
      }).finally(() => this.paymentProcessing = false);
    },
    scheduleMeeting() {
      this.$store.commit('onboarding/setWhiteGloveService');
      window.open('https://api.leadconnectorhq.com/widget/bookings/forefront-crm-onboarding', '_blank');
      this.$router.push({name: 'onboarding-provisioning'});
    }
  },
})
</script>

<style scoped lang="scss">

.video-container {
  position: relative;
  max-width: 600px;
  margin: 0 auto;
  padding: 0 20px;

  .video {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
  }

  .iframe {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
  }
}

.onboarding_button {
  display: block;
  margin-top: 8px;
  line-height: 38px;
}

.onboarding {

  &--heading {
    margin-bottom: 12px;
    font-size: 2rem;
    text-align: center;
  }

  &--text {
    margin-bottom: 10px;
    line-height: 1.3;
  }

  &--content {
    margin-top: 2rem;
    padding: 0 2rem;
  }

  &--question {
    margin-bottom: 0.5rem;
    line-height: 1.3;
  }
}
</style>
